// O recorte da área de detalhamento pelos meses selecionados no gráfico (design D2), em
// funções puras: quanto um item contribui nos meses escolhidos, quais itens entram no
// recorte e como repartir um ajuste pelos meses de origem. Sem requisição, sem Date.
import { competenceOfLabel } from "./months";

const pointCompetence = (point) => {
  if (Number.isInteger(point?.year) && Number.isInteger(point?.month)) {
    return `${point.year}-${String(point.month).padStart(2, "0")}`;
  }
  return competenceOfLabel(point?.date) ?? null;
};

/**
 * Valor, em REAIS, que `points` (`[{ year, month, value }]` ou `[{ date, value }]`) soma
 * nos meses de `selectedMonths` (`Set<"AAAA-MM">`). Meses fora da seleção, pontos malformados
 * e valores não numéricos não contam.
 */
export const contributionOf = (points, selectedMonths) => {
  if (!Array.isArray(points) || !(selectedMonths instanceof Set)) return 0;
  let total = 0;
  for (const point of points) {
    const competence = pointCompetence(point);
    const value = Number(point?.value);
    if (competence && selectedMonths.has(competence) && Number.isFinite(value)) total += value;
  }
  return total;
};

/**
 * Valor de cada mês SELECIONADO, em centavos inteiros, ordenado por competência — só os
 * meses em que o item tem valor. É a origem do ajuste a partir do recorte.
 */
export const monthlyAvailability = (points, selectedMonths) => {
  const byMonth = new Map();
  for (const point of Array.isArray(points) ? points : []) {
    const competence = pointCompetence(point);
    const value = Number(point?.value);
    if (!competence || !selectedMonths.has(competence) || !Number.isFinite(value)) continue;
    byMonth.set(competence, (byMonth.get(competence) ?? 0) + Math.round(value * 100));
  }
  return [...byMonth.entries()]
    .filter(([, cents]) => cents > 0)
    .sort(([left], [right]) => (left < right ? -1 : 1))
    .map(([competence, availableCents]) => ({ competence, availableCents }));
};

/** Itens com valor em algum dos meses selecionados. Sem seleção, a lista inteira. */
export const sliceItems = (items, selectedMonths, pointsOf) => {
  if (!(selectedMonths instanceof Set) || selectedMonths.size === 0) return items;
  return items.filter((item) => contributionOf(pointsOf(item), selectedMonths) > 0);
};

/**
 * Reparte `amountCents` pelos meses de origem, do mais antigo ao mais recente, sem passar do
 * que cada um tem disponível. Devolve `[{ competence, amountCents }]` somando EXATAMENTE
 * `amountCents`, ou `null` quando o valor é inválido ou passa do total disponível — a tela
 * nunca oferece um valor que nenhuma combinação de meses cobre.
 */
export const splitAcrossMonths = (amountCents, availability) => {
  if (!Number.isSafeInteger(amountCents) || amountCents <= 0) return null;
  const total = availability.reduce((sum, month) => sum + month.availableCents, 0);
  if (amountCents > total) return null;
  const lines = [];
  let remaining = amountCents;
  for (const { competence, availableCents } of availability) {
    if (remaining === 0) break;
    const take = Math.min(remaining, availableCents);
    lines.push({ competence, amountCents: take });
    remaining -= take;
  }
  return lines;
};
